import React from 'react';
import { Box, Typography, Paper, IconButton, Divider } from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import ContentTypeSelector from './ContentTypeSelector';
import FlippingCardsEditor from './FlippingCardsEditor';
import StandardContentEditor from './StandardContentEditor';
import LabyrinthContentEditor from './LabyrinthContentEditor';
import MemoryGameEditor from './MemoryGameEditor';
import TagCloudEditor from './TagCloudEditor';
import Button from './Button';
import TextField from './TextField';
import RolePlayGameEditor from './RolePlayGameEditor';
import HtmlContentEditor from './HtmlContentEditor';
import CustomHtmlContentEditor from './CustomHtmlContentEditor';
import VideoContentEditor from './VideoContentEditor';
import { useTranslation } from 'react-i18next';

const ContentBlocksEditor = ({
  blocks = [],
  onChange,
  onAddBlock,
  onRemoveBlock,
  onMoveBlock,
  onSwitchToSingle,
  isMultiBlockMode = false,
}) => {
  const { t } = useTranslation(['lessons']);

  const handleBlockTypeChange = (index, type) => {
    const updatedBlocks = [...blocks];

    // Если меняется тип, может потребоваться обновить содержимое с шаблоном
    if (updatedBlocks[index].type !== type) {
      let content = updatedBlocks[index].content;

      // Предоставляем шаблоны для разных типов контента
      if (
        type === 'flippingCards' &&
        (updatedBlocks[index].type === 'standard' || !content)
      ) {
        content = JSON.stringify(
          [
            {
              front: {
                title: 'Вопрос',
                content: '<p>Введите вопрос здесь</p>',
              },
              back: { title: 'Ответ', content: '<p>Введите ответ здесь</p>' },
            },
          ],
          null,
          2
        );
      } else if (
        type === 'labyrinth' &&
        (updatedBlocks[index].type === 'standard' || !content)
      ) {
        content = JSON.stringify(
          {
            title: 'Интерактивный лабиринт',
            startNode: {
              id: 'start',
              title: 'Начало',
              content: '<p>Выберите путь:</p>',
              options: [
                { text: 'Путь 1', nextNodeId: 'path1' },
                { text: 'Путь 2', nextNodeId: 'path2' },
              ],
            },
            nodes: [
              {
                id: 'path1',
                title: 'Путь 1',
                content: '<p>Содержимое для пути 1</p>',
                options: [{ text: 'Завершить', nextNodeId: 'end' }],
              },
              {
                id: 'path2',
                title: 'Путь 2',
                content: '<p>Содержимое для пути 2</p>',
                options: [{ text: 'Завершить', nextNodeId: 'end' }],
              },
              {
                id: 'end',
                title: 'Завершение',
                content: '<p>Вы завершили изучение!</p>',
                isEndNode: true,
              },
            ],
          },
          null,
          2
        );
      } else if (
        type === 'html' &&
        (!content || updatedBlocks[index].type === 'standard')
      ) {
        content = '<div>Введите HTML-контент блока</div>';
      } else if (
        type === 'customHtml' &&
        (!content || updatedBlocks[index].type === 'standard')
      ) {
        content = '<div>Введите Custom HTML-контент блока</div>';
      } else if (type === 'standard' && !content) {
        // Для стандартного типа устанавливаем простой текст по умолчанию
        content = '';
      }

      updatedBlocks[index] = {
        ...updatedBlocks[index],
        type,
        content,
      };
    } else {
      updatedBlocks[index] = {
        ...updatedBlocks[index],
        type,
      };
    }

    onChange(updatedBlocks);
  };

  const handleBlockContentChange = (index, content) => {
    const updatedBlocks = [...blocks];
    updatedBlocks[index] = {
      ...updatedBlocks[index],
      content,
    };
    onChange(updatedBlocks);
  };

  const moveBlockUp = (index) => {
    if (index === 0) return;

    const updatedBlocks = [...blocks];
    const blockToMove = updatedBlocks[index];
    updatedBlocks.splice(index, 1);
    updatedBlocks.splice(index - 1, 0, blockToMove);

    // Обновляем порядок
    updatedBlocks.forEach((block, idx) => {
      block.order = idx;
    });

    onChange(updatedBlocks);
    if (onMoveBlock) onMoveBlock(updatedBlocks);
  };

  const moveBlockDown = (index) => {
    if (index === blocks.length - 1) return;

    const updatedBlocks = [...blocks];
    const blockToMove = updatedBlocks[index];
    updatedBlocks.splice(index, 1);
    updatedBlocks.splice(index + 1, 0, blockToMove);

    // Обновляем порядок
    updatedBlocks.forEach((block, idx) => {
      block.order = idx;
    });

    onChange(updatedBlocks);
    if (onMoveBlock) onMoveBlock(updatedBlocks);
  };

  // Рендерим редактор в зависимости от типа блока
  const renderEditorForType = (block, index) => {
    switch (block.type) {
      case 'standard':
        return (
          <StandardContentEditor
            content={block.content}
            onChange={(value) => handleBlockContentChange(index, value)}
          />
        );
      case 'presentation':
      case 'htmlBlock':
        return (
          <HtmlContentEditor
            content={block.content}
            onChange={(value) => handleBlockContentChange(index, value)}
          />
        );
      case 'fullHtml':
        return (
          <CustomHtmlContentEditor
            content={block.content}
            onChange={(value) => handleBlockContentChange(index, value)}
          />
        );
      case 'flippingCards':
        return (
          <FlippingCardsEditor
            content={block.content}
            onChange={(value) => handleBlockContentChange(index, value)}
          />
        );
      case 'labyrinth':
        return (
          <LabyrinthContentEditor
            content={block.content}
            onChange={(value) => handleBlockContentChange(index, value)}
          />
        );
      case 'memoryGame':
        return (
          <MemoryGameEditor
            content={block.content}
            onChange={(value) => handleBlockContentChange(index, value)}
          />
        );
      case 'tagCloud':
        return (
          <TagCloudEditor
            content={block.content}
            onChange={(value) => handleBlockContentChange(index, value)}
          />
        );
      case 'rolePlayGame':
        return (
          <RolePlayGameEditor
            content={block.content}
            onChange={(value) => handleBlockContentChange(index, value)}
          />
        );
      case 'video':
        return (
          <VideoContentEditor
            content={block.content}
            onChange={(value) => handleBlockContentChange(index, value)}
          />
        );
      default:
        return (
          <TextField
            fullWidth
            multiline
            rows={5}
            label="Содержимое блока"
            value={block.content}
            onChange={(e) => handleBlockContentChange(index, e.target.value)}
          />
        );
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          {t('lessonEditor.contentBlocksTitle')}
        </Typography>
        {isMultiBlockMode && onSwitchToSingle && (
          <Button
            variant="outlined"
            size="small"
            onClick={onSwitchToSingle}
          >
            {t('lessonEditor.switchToSingle')}
          </Button>
        )}
      </Box>

      {blocks.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', mb: 2 }}>
          <Typography color="text.secondary" gutterBottom>
            {t('lessonEditor.noContentBlocks')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddBlock}
          >
            {t('lessonEditor.addFirstContentBlock')}
          </Button>
        </Paper>
      ) : (
        <>
          {blocks.map((block, index) => (
            <Paper
              key={index}
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                border: '1px solid rgba(0, 0, 0, 0.12)',
                position: 'relative',
              }}
            >
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton size="small" sx={{ mr: 1 }}>
                    <DragIcon />
                  </IconButton>
                  <Typography variant="subtitle1">
                    {t('lessonEditor.contentBlockNumber', {
                      number: index + 1,
                    })}
                  </Typography>
                </Box>

                <Box>
                  <IconButton
                    size="small"
                    disabled={index === 0}
                    onClick={() => moveBlockUp(index)}
                    sx={{ mr: 1 }}
                  >
                    ↑
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={index === blocks.length - 1}
                    onClick={() => moveBlockDown(index)}
                    sx={{ mr: 1 }}
                  >
                    ↓
                  </IconButton>
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => onRemoveBlock(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('lessonEditor.contentBlockType')}
                </Typography>
                <ContentTypeSelector
                  selectedType={block.type}
                  onChange={(type) => handleBlockTypeChange(index, type)}
                  isForBlock={true}
                />
              </Box>

              {renderEditorForType(block, index)}
            </Paper>
          ))}

          <Button
            variant="outlined"
            fullWidth
            startIcon={<AddIcon />}
            onClick={onAddBlock}
            sx={{ mb: 2 }}
          >
            {t('lessonEditor.addContentBlock')}
          </Button>
        </>
      )}
    </Box>
  );
};

export default ContentBlocksEditor;
